function formatMoney(cents, currencyCode) {
  const currency =
    currencyCode ||
    window.Shopify?.currency?.active ||
    'USD';
  // Whole amounts drop the decimals ("$70"); any other amount shows both ("$47.60", not "$47.6"),
  // matching snippets/price-format.liquid, which renders these totals before the first cart update.
  const fractionDigits = Number(cents) % 100 === 0 ? 0 : 2;
  try {
    // Force 'en-US' locale to ensure a dot is used for decimals instead of a comma
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(Number(cents) / 100);
  } catch (e) {
    const value = (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
    return value + ' ' + currency;
  }
}

function getCart() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

/* Set on <html> while the drawer is open so the page behind cannot scroll.
   The quick view opens inside an already-open drawer, so this one lock covers
   it too — it does not need its own. */
const SCROLL_LOCK_CLASS = 'component-cart-drawer-open';

export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestStart = this.onCartRequestStart.bind(this);
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-start', this.onCartRequestStart);
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
    this.addEventListener('click', this.onLineClick, true);
    this.refreshFromCart();
    this.initAnnouncementBar();
    this.watchOpenState();
  }

  /* Alpine toggles `cart-open` on this element, so that class is the single
     source of truth for whether the drawer is showing — whichever path opened
     or closed it. */
  watchOpenState() {
    const sync = () => {
      const page = document.documentElement;
      const open = this.classList.contains('cart-open');
      /* Alpine rewrites the class attribute often; bail unless the open state
         actually changed, or a re-measure would read 0 and shift the page. */
      if (open === page.classList.contains(SCROLL_LOCK_CLASS)) return;

      if (open) {
        // Measure while the scrollbar still exists.
        page.style.setProperty('--hy-scrollbar-width', `${window.innerWidth - page.clientWidth}px`);
      } else {
        page.style.removeProperty('--hy-scrollbar-width');
      }
      page.classList.toggle(SCROLL_LOCK_CLASS, open);
    };

    this.openStateObserver = new MutationObserver(sync);
    this.openStateObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-start', this.onCartRequestStart);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
    this.removeEventListener('click', this.onLineClick, true);
    this.openStateObserver?.disconnect();
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    document.documentElement.style.removeProperty('--hy-scrollbar-width');
  }

  /* Capture-phase click handler: mark the affected line as loading immediately,
     before the ajax-cart library mutates the input value optimistically.
     Also resolves photo links lazily to avoid bloating cart section HTML. */
  onLineClick = (event) => {
    const photoLink = event.target.closest('[data-cart-photo-link]');
    if (photoLink) {
      event.preventDefault();
      const lineKey = photoLink.getAttribute('data-line-key');
      getCart()
        .then((cart) => {
          const item = (cart.items || []).find((i) => i.key === lineKey);
          const photo = item?.properties?.Photo;
          if (photo) window.open(photo, '_blank', 'noopener');
        })
        .catch(() => {});
      return;
    }

    const target = event.target.closest(
      '[data-ajax-cart-quantity-plus], [data-ajax-cart-quantity-minus], [data-ajax-cart-request-button]'
    );
    if (!target) return;
    const line = target.closest('.hy-line');

    // Changing a parent line quantity must also change the add-on (mini-heart) units it
    // contributed, by the same amount: removing drops them by the full parent quantity,
    // minus drops them by one, plus raises them by one. Shared add-ons merge into one
    // line, so we adjust by the delta rather than zeroing, keeping other parents' units.
    const isRemove = !!target.closest('[data-ajax-cart-request-button]');
    const isMinus = !!target.closest('[data-ajax-cart-quantity-minus]');
    const isPlus = !!target.closest('[data-ajax-cart-quantity-plus]');
    if ((isRemove || isMinus || isPlus) && line) {
      const parentKey = line.getAttribute('data-line-key');
      const childKeys = (line.getAttribute('data-addon-children') || '')
        .split(',')
        .filter(Boolean);
      if (parentKey && childKeys.length && window.liquidAjaxCart?.update) {
        event.preventDefault();
        event.stopPropagation();
        line.setAttribute('data-loading', 'true');
        childKeys.forEach((key) => {
          this.querySelector(`.hy-line[data-line-key="${CSS.escape(key)}"]`)
            ?.setAttribute('data-loading', 'true');
        });

        const applyUpdates = (updates) => window.liquidAjaxCart.update({ updates });

        getCart()
          .then((cart) => {
            const items = cart.items || [];
            const parent = items.find((i) => i.key === parentKey);
            const parentQty = parent ? parent.quantity : 1;
            let delta;
            if (isRemove) delta = -parentQty;
            else if (isMinus) delta = -1;
            else delta = 1;
            const updates = { [parentKey]: Math.max(0, parentQty + delta) };
            childKeys.forEach((key) => {
              const child = items.find((i) => i.key === key);
              if (!child) return;
              updates[key] = Math.max(0, child.quantity + delta);
            });
            applyUpdates(updates);
          })
          .catch(() => applyUpdates({ [parentKey]: isRemove ? 0 : undefined }));
        return;
      }
    }

    if (line) line.setAttribute('data-loading', 'true');
  };

  /* Resolve which line element a request targets, from the request URL/body. */
  getLineForRequest(requestState) {
    if (!requestState) return null;

    const readParam = (key) => {
      const body = requestState.requestBody;
      if (body instanceof FormData || body instanceof URLSearchParams) {
        return body.has(key) ? String(body.get(key)) : null;
      }
      if (body && typeof body === 'object' && key in body) {
        return String(body[key]);
      }
      if (typeof body === 'string') {
        try {
          const params = new URLSearchParams(body);
          if (params.has(key)) return params.get(key);
        } catch (_) {}
      }
      const endpoint = requestState.endpoint || '';
      if (typeof endpoint === 'string' && endpoint.includes('?')) {
        try {
          const params = new URLSearchParams(endpoint.split('?')[1]);
          if (params.has(key)) return params.get(key);
        } catch (_) {}
      }
      return null;
    };

    const lineParam = readParam('line');
    if (lineParam) {
      const idx = parseInt(lineParam, 10);
      const items = this.querySelectorAll('.hy-line');
      if (items[idx - 1]) return items[idx - 1];
    }

    const idParam = readParam('id');
    if (idParam) {
      const byKey = this.querySelector(`.hy-line[data-line-key="${idParam}"]`);
      if (byKey) return byKey;
    }

    return null;
  }

  onCartRequestStart(event) {
    const { requestState } = event.detail || {};
    const type = requestState?.requestType;

    // Optimistically show totals as soon as an add starts
    if (type === 'add') {
      const totals = this.querySelector('.hy-totals');
      if (totals) totals.style.display = 'flex';
    }

    if (type !== 'change' && type !== 'update') return;

    const line = this.getLineForRequest(requestState);
    if (line) {
      line.setAttribute('data-loading', 'true');
    }
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};
    const ok = requestState?.responseData?.ok;
    const body = requestState?.responseData?.body;

    // A cart mutation (the free gift) can add on page load, so only a shopper's add opens the drawer.
    if (requestState?.requestType === 'add' && ok && requestState.info?.initiator !== 'mutation') {
      window.dispatchEvent(new CustomEvent('cart-open'));
    }

    // Clear any stale loading flags
    this.querySelectorAll('.hy-line[data-loading="true"], .free-gift-card[data-loading="true"]').forEach((el) =>
      el.removeAttribute('data-loading')
    );

    // Use response data directly when available to avoid an extra /cart.js fetch.
    // Only `change`/`update` responses return the full cart object; `add` returns
    // the added line item, so fetch the cart for those.
    const type = requestState?.requestType;
    if (ok && body && (type === 'change' || type === 'update')) {
      this.updateFreeShipping(body);
      this.updateDiscountsAndTotals(body);
    } else {
      this.refreshFromCart();
    }
  }

  refreshFromCart() {
    getCart().then((cart) => {
      this.updateFreeShipping(cart);
      this.updateDiscountsAndTotals(cart);
    }).catch(() => {});
  }

  /* ---------- Announcement bar ---------- */
  initAnnouncementBar() {
    const texts = this.querySelectorAll('.hy-announcement-bar__text');
    if (texts.length < 2) return;
    let index = 0;
    setInterval(() => {
      texts[index].hidden = true;
      index = (index + 1) % texts.length;
      texts[index].hidden = false;
    }, 4000);
  }

  /* ---------- Free shipping meter ---------- */
  updateFreeShipping(cart) {
    if (this.dataset.fsEnabled !== 'true') return;
    const wrap = this.querySelector('[data-cart-free-shipping]');
    const fill = this.querySelector('[data-cart-free-shipping-fill]');
    const msg = this.querySelector('[data-cart-free-shipping-msg]');
    if (!wrap || !fill || !msg) return;

    const isEmpty = this.isEmpty(cart);
    const wasHidden = wrap.hidden;
    wrap.hidden = isEmpty;

    const threshold = parseInt(this.dataset.fsThreshold || '0', 10);
    if (!threshold) return;

    const total = cart.items_subtotal_price || cart.total_price || 0;

    const ratio = Math.min(1, total / threshold);
    if (wasHidden && !isEmpty) {
      const prev = fill.style.transition;
      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${ratio})`;
      fill.offsetWidth;
      fill.style.transition = prev;
    } else {
      fill.style.transform = `scaleX(${ratio})`;
    }

    if (ratio >= 1) {
      const qualifiedColor = this.dataset.fsBarQualifiedColor || '#878F79';
      fill.style.backgroundColor = qualifiedColor;
      this.setQualifiedMessage(
        msg,
        this.dataset.fsQualifiedMessage || 'Congrats! You unlocked free shipping.',
        qualifiedColor
      );
    } else {
      fill.style.backgroundColor = this.dataset.fsBarColor || '#9d8268';
      const remaining = Math.max(0, threshold - total);
      this.setProgressMessage(
        msg,
        this.dataset.fsProgressMessage || "You're {{amount}} from free shipping",
        formatMoney(remaining, cart.currency)
      );
    }
  }

  /* Progress message: the {{amount}} token renders bold. Same textContent-only
     approach as the qualified message. No token in the copy → plain message. */
  setProgressMessage(msg, template, amount) {
    const parts = template.split(/\{\{\s*amount\s*\}\}/);
    if (parts.length === 1) {
      msg.textContent = template;
      return;
    }
    const value = document.createElement('span');
    value.className = 'hy-free-shipping__msg-amount';
    value.textContent = amount;
    msg.textContent = '';
    msg.append(parts[0], value, parts.slice(1).join(''));
  }

  /* Qualified message: the leading exclamation phrase (e.g. "Congrats!") takes the
     same color as the progress bar. Text is set via textContent so merchant copy is
     never treated as HTML. No "!" in the copy → plain message, no highlight. */
  setQualifiedMessage(msg, text, color) {
    const breakAt = text.indexOf('!');
    if (breakAt === -1) {
      msg.textContent = text;
      return;
    }
    const lead = document.createElement('span');
    lead.className = 'hy-free-shipping__msg-lead';
    lead.style.color = color;
    lead.textContent = text.slice(0, breakAt + 1);
    msg.textContent = '';
    msg.append(lead, text.slice(breakAt + 1));
  }

  /**
   * A cart holding nothing but the free gift counts as empty: the gift's cart mutation
   * (component-cart-free-gift.js) is about to remove it. Matches `cart_empty` in the drawer snippet.
   * @param {{ item_count: number, items?: Array<{ variant_id: number, properties?: object }> }} cart
   * @returns {boolean}
   */
  isEmpty(cart) {
    const gift = this.querySelector('cart-free-gift');
    if (!gift || !cart.items) return !cart.item_count;

    const giftVariantId = Number(gift.dataset.variantId);
    return !cart.items.some((item) => item.variant_id !== giftVariantId && !item.properties?._free_gift);
  }

  /* ---------- Discounts and Totals visibility ---------- */
  updateDiscountsAndTotals(cart) {
    const discounts = this.querySelector('.hy-discounts');
    const totals = this.querySelector('.hy-totals');
    const subtotal = this.querySelector('[data-cart-subtotal]');
    const count = this.querySelector('[data-cart-drawer-count]');
    const isEmpty = this.isEmpty(cart);

    if (count) {
      count.textContent = isEmpty ? 0 : cart.item_count;
    }
    if (discounts) {
      discounts.style.display = isEmpty ? 'none' : 'flex';
    }
    if (totals) {
      totals.style.display = isEmpty ? 'none' : 'flex';
    }
    if (subtotal) {
      // Use formatMoney to ensure consistent US-style decimal formatting
      subtotal.textContent = formatMoney(cart.total_price || 0, cart.currency);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* Global handler for lazy photo links on cart page / cart notification.
   Resolves the base64 data URL from /cart.js only when the link is clicked. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-cart-photo-link]');
  if (!link) return;
  if (link.closest('cart-drawer')) return; // drawer handles its own
  event.preventDefault();
  const lineKey = link.getAttribute('data-line-key');
  getCart()
    .then((cart) => {
      const item = (cart.items || []).find((i) => i.key === lineKey);
      const photo = item?.properties?.Photo;
      if (photo) window.open(photo, '_blank', 'noopener');
    })
    .catch(() => {});
});
